import React, { useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BlogHero, ArticleList } from '../../components/Blog';
import { getArticlesBySilo } from '../../data/blog';
import { SILOS } from '../../data/blog/silos';
import type { SiloId } from '../../types/blog';
import { trackBlogSiloView } from '../../utils/analytics';

const VALID_SILOS = Object.keys(SILOS) as SiloId[];

const BlogSilo: React.FC = () => {
  const { siloId } = useParams<{ siloId: string }>();

  if (!siloId || !VALID_SILOS.includes(siloId as SiloId)) {
    return <Navigate to="/blog" replace />;
  }

  const silo = SILOS[siloId as SiloId];
  const articles = getArticlesBySilo(siloId as SiloId);

  useEffect(() => {
    const pageTitle = `${silo.title} — Guides & Conseils | SOS IT Services`;
    document.title = pageTitle;

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', `${silo.description} — Guides pratiques et solutions par nos techniciens.`);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `https://sos-it-services.fr/blog/silo/${siloId}`);

    window.scrollTo(0, 0);

    trackBlogSiloView(siloId);
  }, [siloId, silo]);

  return (
    <div className="pt-16 md:pt-20 min-h-screen bg-[#2F3136]">
      <BlogHero
        title={`${silo.title} — Guides & Conseils`}
        subtitle={silo.description}
        activeSilo={siloId as SiloId}
      />

      <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      <section className="py-14 bg-[#2F3136]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
          >
            <h2 className="text-xl md:text-2xl font-bold text-white">
              {silo.title}{' '}
              {articles.length > 0 && (
                <span className="text-gray-400 font-normal text-base">({articles.length})</span>
              )}
            </h2>
            <p className="text-gray-400 text-sm mt-1">{silo.description}</p>
          </motion.div>

          <ArticleList articles={articles} perPage={9} />
        </div>
      </section>
    </div>
  );
};

export default BlogSilo;
